import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AlertTriangle, Clock, PackageCheck, Info } from 'lucide-react';
import { useProximoBoletim, ProximoBoletimItem } from '@/hooks/useProximoBoletim';

import { fmtCurrency as fmtCur } from '@/lib/formatters';
const fmtQty = (v: number, u: string) => `${v.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} ${u}`;

interface ProximoBoletimPanelProps {
  contratoId: string;
}

export const ProximoBoletimPanel: React.FC<ProximoBoletimPanelProps> = ({ contratoId }) => {
  const { data: itens = [], isLoading } = useProximoBoletim(contratoId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">
        Carregando...
      </div>
    );
  }

  if (itens.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-40 text-muted-foreground text-sm gap-2">
        <PackageCheck className="h-8 w-8 opacity-40" />
        <p className="text-center">Não há execuções prontas para medição.<br />Registre execuções no campo ou aguarde validação pela Qualidade.</p>
      </div>
    );
  }

  const totalValor = itens.reduce((s, i) => s + i.valor_disponivel, 0);
  const totalExecucoes = itens.reduce((s, i) => s + i.qtd_execucoes, 0);
  const itensComDefasagem = itens.filter(i => i.defasagem_dias_max > 15).length;

  return (
    <div className="space-y-3">
      {/* FIFO tooltip header */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-help">
              <Info className="h-3.5 w-3.5" />
              <span>Estas execuções já estão validadas e serão incluídas automaticamente no próximo boletim, seguindo ordem de chegada (FIFO).</span>
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs">
            <p>Registros recentes precisam passar pela Qualidade antes de aparecer aqui.</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Items list */}
      <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
        {itens.map((item) => (
          <ProximoBoletimItemCard key={item.planilha_item_id} item={item} />
        ))}
      </div>

      {/* Summary footer */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="py-3 px-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Total elegível</p>
              <p className="text-lg font-bold">{fmtCur(totalValor)}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">{totalExecucoes} execuções</p>
              {itensComDefasagem > 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1 justify-end">
                  <AlertTriangle className="h-3 w-3" />
                  {itensComDefasagem} com defasagem
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const ProximoBoletimItemCard: React.FC<{ item: ProximoBoletimItem }> = ({ item }) => {
  const isWarning = item.defasagem_dias_max > 15 && item.defasagem_dias_max <= 30;
  const isCritical = item.defasagem_dias_max > 30;

  return (
    <div className="p-3 rounded-md border bg-card hover:bg-muted/30 transition-colors">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">
            {item.codigo_item} — {item.item_descricao}
          </p>
          <p className="text-sm text-foreground mt-1">
            {fmtQty(item.quantidade_disponivel, item.unidade)} · {fmtCur(item.valor_disponivel)}
          </p>
          <div className="flex items-center gap-2 mt-1.5 text-xs text-muted-foreground">
            <span>{item.qtd_execucoes} execuç{item.qtd_execucoes === 1 ? 'ão' : 'ões'}</span>
            <span>·</span>
            <span className={`flex items-center gap-1 ${isCritical ? 'text-destructive font-medium' : isWarning ? 'text-amber-600 dark:text-amber-400 font-medium' : ''}`}>
              <Clock className="h-3 w-3" />
              {item.defasagem_dias_max === 0 ? 'sem defasagem' : `defasagem: ${item.defasagem_dias_max}d`}
            </span>
          </div>
        </div>
        {(isWarning || isCritical) && (
          <Badge variant={isCritical ? 'destructive' : 'secondary'} className="shrink-0 text-xs">
            {isCritical ? '🔴 >30d' : '⚠️ >15d'}
          </Badge>
        )}
      </div>
    </div>
  );
};
